import { Link } from "react-router-dom";
import { ArrowUpRight } from "@phosphor-icons/react";
import PageHeader from "../components/PageHeader";
import SEOMeta from "../components/SEOMeta";

export default function Insights() {
  return (
    <div data-testid="insights-page">
      <SEOMeta
        title="Insights"
        description="Strategy insights from Dugavu Ventures on corporate strategy, minerals, mining, oil and gas, and public-sector governance for Africa's leading institutions."
        url="/insights"
      />
      <PageHeader
        eyebrow="Insights"
        title="Insights from Dugavu Ventures."
        lede="Insights from Dugavu Ventures focus on the strategic, governance, and commercial questions facing Africa's state-owned enterprises and resource-sector companies. We publish when we have something to say."
        image="https://images.pexels.com/photos/3184292/pexels-photo-3184292.jpeg?auto=compress&cs=tinysrgb&w=1800&q=80"
        imageAlt="Editorial desk with documents"
        testid="insights-header"
      />

      <section className="py-20 md:py-28" data-testid="insights-list">
        <div className="max-w-[1240px] mx-auto px-6 md:px-10 lg:px-14">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 mb-12">
            <div className="md:col-span-3">
              <div className="eyebrow">Latest</div>
            </div>
            <div className="md:col-span-9">
              <p className="text-[13px] tracking-widest uppercase font-mono text-slate">
                Inaugural publication
              </p>
            </div>
          </div>

          {/* Featured Article */}
          <Link
            to="/insights/sovereign-mineral-strategy"
            data-testid="featured-article-link"
            className="group block border-t border-rule"
          >
            <article className="grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12 py-12 md:py-16">
              <div className="md:col-span-5">
                <div className="aspect-[4/5] overflow-hidden bg-earth-dark/20">
                  <img
                    src="https://images.pexels.com/photos/36224103/pexels-photo-36224103.jpeg?auto=compress&cs=tinysrgb&w=1200&q=80"
                    alt="Aerial view of quarry in Nigeria"
                    className="w-full h-full object-cover photo-treat transition-transform duration-700 group-hover:scale-[1.02]"
                  />
                </div>
              </div>
              <div className="md:col-span-7 flex flex-col justify-between">
                <div>
                  <div className="flex items-center gap-4">
                    <span className="text-[11px] tracking-widest uppercase font-mono text-oxblood">Strategy note</span>
                    <span className="text-[11px] tracking-widest uppercase font-mono text-slate">Mining and minerals</span>
                  </div>
                  <h2 className="display-2 mt-6 text-4xl md:text-5xl lg:text-6xl text-ink leading-tight group-hover:text-oxblood transition-colors duration-500">
                    The sovereign mineral question: when state ownership is the strategy.
                  </h2>
                  <p className="mt-6 text-lg text-charcoal leading-relaxed max-w-2xl">
                    A note on how African governments should think about ownership, mandate, and commercial discipline in the next phase of mineral sector development. Less a question of nationalisation, more a question of institutional design.
                  </p>
                </div>
                <div className="mt-10 flex items-center justify-between">
                  <div className="text-[12px] tracking-widest uppercase font-mono text-slate">
                    By Dugavu Ventures · 12 min read
                  </div>
                  <span className="inline-flex items-center gap-2 text-[12px] tracking-widest uppercase font-medium text-oxblood">
                    Read note
                    <ArrowUpRight size={14} weight="thin" className="transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" />
                  </span>
                </div>
              </div>
            </article>
          </Link>

          <div className="border-t border-rule pt-16 mt-4 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12" data-testid="editorial-themes">
            <div className="md:col-span-3">
              <div className="eyebrow">Editorial themes</div>
            </div>
            <div className="md:col-span-9">
              <h3 className="font-serif text-3xl md:text-4xl text-ink leading-tight max-w-3xl">
                Subjects we expect to address.
              </h3>
              <ul className="mt-10 border-t border-rule">
                {[
                  "SOE governance: how mandate, board, and shareholder relationships shape performance.",
                  "The economics of national mining companies — between policy and the market.",
                  "Local content as commercial strategy, not compliance.",
                  "What public-sector strategy actually looks like, when it works.",
                  "Transformation in capital-intensive industries — programme design and discipline."
                ].map((t, i) => (
                  <li
                    key={t}
                    className="grid grid-cols-1 md:grid-cols-12 gap-6 py-6 border-b border-rule"
                    data-testid={`editorial-theme-${i}`}
                  >
                    <div className="md:col-span-1">
                      <span className="numeral text-xl">0{i + 1}</span>
                    </div>
                    <div className="md:col-span-11">
                      <p className="font-serif text-xl md:text-2xl text-ink leading-snug">{t}</p>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          <div className="mt-20 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-12" data-testid="editorial-cadence">
            <div className="md:col-span-3">
              <div className="eyebrow">Cadence</div>
            </div>
            <div className="md:col-span-9">
              <p className="font-serif text-2xl md:text-3xl text-ink leading-snug max-w-3xl">
                Quarterly briefings, two to three pages each.
              </p>
              <p className="mt-6 text-charcoal text-lg leading-relaxed max-w-2xl">
                Format consistent: framing question, evidence, argument, implications for leaders. No newsletters until there is enough work to draw from.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
